<template>
  <div class="d-flex flex-column mt-3 px-5">
    <div :v-if="description">
      <mrx-translation :token="description" :renderAsHTML="true"></mrx-translation>
    </div>

    <div class="mb-3">
      <mrx-translation class="input-instructions" token="app.inputs.textinput.instructions"
        :renderAsHTML="true"></mrx-translation>
    </div>

    <v-text-field ref="input" id="input" v-model="userInput" :rules="[validate, validateNoDisallowedChars]" :label="inputLabel"
      :placeholder="placeholder" autocomplete="off" outlined dense flat @input="update" :disabled="disabled"
      class="custom-placeholder"></v-text-field>

    <div class="mt-4 align-self-center" v-if="options.allow_unknown">
      <unknown-button id="text-input-unknown" @click="setUnknown"></unknown-button>
    </div>
  </div>
</template>

<script>
import languageLogic from '@/mixins/languageLogic'
import { inputPresent, hasUnpermittedCharacters, matchesPattern } from '@/utils/userInput'
import * as textInputAutofill from '@/utils/textInputAutofill'
import UnknownButton from '@/components/widgets/UnknownButton.vue'
import { mapState } from 'pinia'
import { usePartnerConfigStore } from '@/stores/partnerConfig'
import { useNavigation } from '@/composables/useNavigation';

export default {
  mixins: [languageLogic],
  components: { UnknownButton },
  name: 'textinput',
  model: {
    prop: 'items',
    event: 'click'
  },
  data() {
    return {
      userInput: null,
      valid: value => {
        let isValid = true
        if (this.items[0].unknown) {
          return true
        }

        if (!inputPresent(value)) {
          isValid = false
        }
        else if (this.options.inputValidation && this.options.inputValidation.length > 0) {
          isValid = matchesPattern(this.options.inputValidation, value)
        }
        return isValid
      },
      validate: value => this.valid(value) || this.errorMessage,
      validateNoDisallowedChars: value => !hasUnpermittedCharacters(value) ||
        this.$t('app.error.textinput.characters.title'),
    }
  },
  props: {
    'options': { type: Object, default: {} },
    'items': Array,
    'panelName': String,
    'embedded': { type: Boolean, default: false },
  },
  setup() {
    const { next } = useNavigation();
    return { next };
  },
  computed: {
    disabled() {
      return ('autofill' in this.options)
    },

    description() {
      return `questions.${this.panelName}.input_description`
    },

    errorMessage() {
      return this.$t(`questions.${this.panelName}.error`)
    },

    placeholder() {
      return this.items[0].unknown ? this.$i18n.t('app.inputs.unknown.display') : null
    },

    inputLabel() {
      return this.$te(`questions.${this.panelName}.input_label`, this.$i18n.fallbackLocale) ? this.$t(`questions.${this.panelName}.input_label`) : ''
    },

    storedValueUnmasked() {
      return this.items[0].value ? this.items[0].value.split('.').join('') : null
    },

    ...mapState(usePartnerConfigStore, ['autoProgress'])
  },
  methods: {
    update() {
      this.items[0].selected = false
      this.items[0].unknown = false
      if (this.valid(this.userInput) && !hasUnpermittedCharacters(this.userInput)) {
        this.items[0].value = this.userInput.toString()
        this.items[0].selected = true
        this.$track(this.$analyticsEvents.TEXT_INPUT_COMPLETED, {
          component: 'TextInput',
          panel_name: this.panelName,
          input_length: this.userInput.length,
          has_validation: !!this.options.inputValidation,
          is_autofilled: 'autofill' in this.options
        })
      } else {
        if (this.userInput && this.userInput.length > 0) {
          this.$track(this.$analyticsEvents.TEXT_INPUT_VALIDATION_FAILED, {
            component: 'TextInput',
            panel_name: this.panelName,
            input_length: this.userInput.length,
            has_unpermitted_chars: hasUnpermittedCharacters(this.userInput),
            validation_pattern: this.options.inputValidation || null
          })
        }
      }
    },
    setUnknown() {
      this.userInput = null
      this.items[0].value = null
      this.items[0].unknown = true
      this.items[0].selected = true
      this.$track(this.$analyticsEvents.TEXT_INPUT_UNKNOWN_SELECTED, {
        component: 'TextInput',
        panel_name: this.panelName,
        auto_progressed: this.autoProgress
      })

      if (!this.embedded && this.autoProgress) {
        this.next()
      }
    },
    set() {
      if (this.items[0].unknown) {
        this.setUnknown()
        return
      }

      this.userInput = null

      if (this.items[0].value) {
        this.userInput = this.items[0].value
      } else if ('autofill' in this.options) {
        try {
          this.userInput = textInputAutofill[this.options.autofill]()
        } catch (e) {
          // autofill function not implemented on app side
          console.log('Error: Autofill function does not exist.')
        }
      }
      this.update()
    },
  },

  created() {
    this.set()
  },

  watch: {
    panelName: function (val) {
      this.set()
    },
    errorMessage() {
      this.$nextTick(() => this.$refs.input?.validate())
    }
  }
}
</script>

<style scoped>
::v-deep .custom-placeholder .v-text-field__slot input::placeholder {
  color: var(--primaryblue);
  opacity: 0.66;
}
</style>
